"use client";

import Link from "next/link";
import { useAuth } from "@/lib/auth-context";
import { logEvent } from "@/lib/analytics";
import { NOTE_CTA, noteCtaTitle, splitEmphasis } from "@/lib/note/copy";
import {
  HOME_CARD_RADIUS,
  HOME_CTA_SHAPE_CLASS,
  HOME_EYEBROW_CLASS,
  HOME_EYEBROW_STYLE,
  HOME_MAGENTA_BTN_CLASS,
  HOME_SECTION_BG,
  HOME_TITLE_CLASS,
  HOME_TITLE_EM_CLASS,
  HOME_TITLE_STYLE,
} from "@/components/home-style";

/**
 * 글 하단 모의고사 CTA 박스 (2026-10-12 #47 · #47-2 시안 A) — 상세 본문 끝 · 좋아요/공유·이전/다음 글 위 (src/app/note/[slug]/page.tsx).
 * 본문 문자열이 아니라 렌더 단계에서 붙인다 (기존 글·자동 발행 글 모두 · Firestore 수정 0).
 * 소제목 · 2줄 제목 · 버튼 3줄만 (보조문구·부제 금지) · 문구 copy.ts::NOTE_CTA.
 * 스타일은 홈과 공용 (src/components/home-style.ts): 배경·모서리 = 홈 'The Problem' 섹션·카드 · 소제목·제목 = 그 섹션 소제목·제목 ·
 * 버튼 = 홈 히어로 무료 체험 버튼 모양 + 홈 상단 Start 버튼 색·반짝임 (.qa-shimmer · 흰 글자 대비 띠 포함 4.5 이상).
 * 목적지 = 홈 히어로 "무료 체험하기" 와 같다 (src/app/page.tsx Hero heroPrimary · 로그인 /dashboard · 비로그인 /signup).
 * 클릭은 analytics_events 에 note_cta_click {slug, subject} (src/lib/analytics.ts::logEvent).
 */
export default function NoteCta({ slug, subject }: { slug: string; subject: string | null }) {
  const { user } = useAuth();
  const href = user ? "/dashboard" : "/signup";
  const lines = noteCtaTitle(subject);
  return (
    <section
      className="mt-10 px-3 py-10 lg:px-10 lg:py-14 text-center"
      style={{ background: HOME_SECTION_BG, borderRadius: HOME_CARD_RADIUS }}
      data-testid="note-cta"
    >
      <div className={`${HOME_EYEBROW_CLASS} mb-4`} style={HOME_EYEBROW_STYLE} data-testid="note-cta-eyebrow">
        {NOTE_CTA.eyebrow}
      </div>
      <h2 className={HOME_TITLE_CLASS} style={HOME_TITLE_STYLE} data-testid="note-cta-title">
        {lines.map((line, i) => (
          <span key={i} className="block" data-cta-line={i}>
            {splitEmphasis(line).map((p, j) =>
              p.em ? (
                <span key={j} className={HOME_TITLE_EM_CLASS}>
                  {p.text}
                </span>
              ) : (
                p.text
              ),
            )}
          </span>
        ))}
      </h2>
      <Link
        href={href}
        onClick={() => void logEvent("note_cta_click", { slug, subject: subject ?? null })}
        className={`qa-shimmer ${HOME_CTA_SHAPE_CLASS} ${HOME_MAGENTA_BTN_CLASS} mt-8 w-full lg:w-auto transition-colors duration-150`}
        data-testid="note-cta-button"
      >
        <span>{NOTE_CTA.button}</span>
      </Link>
    </section>
  );
}
